/**
 * Pure calculation functions for bar processing
 * Following Uncle Bob's principles: single responsibility, no side effects
 */

import { findHousePinWeightForBar, PIN_KINDS } from './refiningLotPinState'
import { extractDisplayWeight, convertFireSampleToTroyOunces } from './weightUtils'

/**
 * Calculate total weight from gross and pin weights
 * @param {object|number|string} grossWeight - The gross weight (rich weight object or legacy number)
 * @param {object|number|string} pinWeight - The pin weight (rich weight object or legacy number)
 * @returns {number} - Total process weight in troy ounces
 */
export const calculateTotalProcessWeight = (grossWeight, pinWeight) => {
  const gross = extractDisplayWeight(grossWeight, 'troyOunces') || 0
  const pin = extractDisplayWeight(pinWeight, 'troyOunces') || 0
  return gross + pin
}

/**
 * Calculate total weight for a collection of weighed records (bars or pins)
 * @param {Array} records - Array of records carrying the weight field
 * @param {string} weightField - The weight field to sum ('grossWeight', 'assayLoss', etc.)
 * @returns {number} - Total weight in troy ounces
 */
export const calculateTotalWeight = (records, weightField) => {
  return records.reduce((sum, bar) => {
    return sum + (extractDisplayWeight(bar[weightField], 'troyOunces') || 0)
  }, 0)
}

/**
 * Calculate total process weight (bar gross + house pin gross) for all bars
 * @param {Array} bars - Array of bar objects
 * @param {Array} pins - Canonical pin rows for the lot
 * @returns {number} - Total process weight in troy ounces
 */
export const calculateTotalProcessWeightForBars = (bars, pins) => {
  return bars.reduce((sum, bar) => {
    const gross = extractDisplayWeight(bar.grossWeight, 'troyOunces') || 0
    const pin = extractDisplayWeight(findHousePinWeightForBar(bar, pins), 'troyOunces') || 0
    return sum + gross + pin
  }, 0)
}

/**
 * Calculate melt loss from received and process weights
 * Formula: received_weight - process_weight (matches backend MELT_LOSS_EXPRESSION)
 * @param {object|number|null} receivedWeight - Starting/received gross weight (rich weight object or legacy number)
 * @param {object|number|string} postMeltWeight - Post-melt weight (rich weight object or legacy number)
 * @returns {number|null} - Melt loss in troy ounces, or null if received weight is null
 */
export const calculateMeltLoss = (receivedWeight, postMeltWeight) => {
  if (receivedWeight === null) return null
  const received = extractDisplayWeight(receivedWeight, 'troyOunces') || 0
  const postMelt = extractDisplayWeight(postMeltWeight, 'troyOunces') || 0
  return received - postMelt
}

/**
 * Calculate melt loss percentage from starting and after-melt weights
 * Melt Loss % = (Melt Loss / Total Gross Weight) * 100
 * Here, Total Gross Weight corresponds to the starting gross weight
 * @param {object|number|null} startingWeight - Starting gross weight
 * @param {object|number|string} afterMeltWeight - After melt gross weight
 * @returns {string} - Percentage to 2 decimals, or '--' if unavailable
 */
export const calculateMeltLossPercent = (startingWeight, afterMeltWeight) => {
  if (startingWeight === null) return '--'
  const starting = extractDisplayWeight(startingWeight, 'troyOunces') || 0
  if (!starting) return '--'
  const after = extractDisplayWeight(afterMeltWeight, 'troyOunces') || 0
  const loss = starting - after
  return ((loss / starting) * 100).toFixed(2)
}

/**
 * Calculate total final pin weight from fire assay results
 * @param {Object} fireResults - Fire results keyed by barNumber
 * @returns {number} - Total final pin weight in troy ounces
 */
export const calculateTotalFinalPinWeight = (fireResults) => {
  let totalFinalPin = 0
  
  Object.values(fireResults || {}).forEach(barData => {
    if (!barData) return
    
    Object.values(barData).forEach(sample => {
      if (sample && typeof sample === 'object' && !sample.outlier) {
        // Fire assay samples are in grams, convert to troy ounces
        const netWeight = extractDisplayWeight(sample.net, 'grams') ?? 
                         (typeof sample.net === 'string' ? parseFloat(sample.net) : sample.net) ?? 0
        totalFinalPin += convertFireSampleToTroyOunces(netWeight)
      }
    })
  })
  
  return totalFinalPin
}

/**
 * Calculate assay loss from pin-level data
 * Formula: sum of assay_loss across house pins (customer pins are never assayed)
 * @param {Array} pins - Canonical pin rows with assayLoss
 * @returns {number} - Total assay loss in troy ounces
 */
export const calculateAssayLoss = (pins) => {
  return (pins || []).reduce((sum, pin) => {
    if (pin.kind !== PIN_KINDS.HOUSE) return sum
    const assayLoss = extractDisplayWeight(pin.assayLoss, 'troyOunces') || 0
    return sum + assayLoss
  }, 0)
}

/**
 * Calculate assay loss for a single bar from fire results
 * Formula: sum of (gross - net) for each sample (cup or dore)
 * Each sample is weighed before (gross) and after (net) fire assay
 * @param {number} barNumber - Bar number to calculate for
 * @param {object} fireResults - Fire results keyed by barNumber
 * @param {boolean} isPrimarySilver - Whether primary metal is silver
 * @returns {number} - Assay loss in troy ounces, or 0 if no fire results
 */
export const calculateAssayLossFromFireResults = (barNumber, fireResults, isPrimarySilver = false) => {
  if (!fireResults) return 0
  
  const barFireData = fireResults[String(barNumber)] || {}
  const samples = []
  
  // Convert nested object to flat array of samples
  Object.entries(barFireData).forEach(([key, sampleData]) => {
    if (sampleData && sampleData.gross && sampleData.net) {
      // silver dore (control) was not customer material, so is ignored
      if (key === 'control' && isPrimarySilver) {
        return
      }
      samples.push(sampleData)
    }
  })
  
  if (!samples.length) return 0
  
  // Calculate loss per sample: gross - net (in grams)
  const totalLossGrams = samples.reduce((sum, sample) => {
    const grossGrams = extractDisplayWeight(sample.gross, 'grams') || 0
    const netGrams = extractDisplayWeight(sample.net, 'grams') || 0
    const sampleLoss = grossGrams - netGrams
    return sum + sampleLoss
  }, 0)
  
  // Convert total loss to troy ounces
  return Math.max(0, convertFireSampleToTroyOunces(totalLossGrams))
}

/**
 * Calculate after melt net weight (after melt gross - customer pin)
 * @param {object|number|string} afterMeltGross - After melt gross weight (rich weight object or legacy number)
 * @param {object|number|string} customerPinWeight - Customer pin weight (rich weight object or legacy number)
 * @returns {number} - Net weight in troy ounces
 */
export const calculateAfterMeltNet = (afterMeltGross, assayLoss, customerPinWeight) => {
  const gross = extractDisplayWeight(afterMeltGross, 'troyOunces') || 0
  const loss = extractDisplayWeight(assayLoss, 'troyOunces') || 0
  const pin = extractDisplayWeight(customerPinWeight, 'troyOunces') || 0
  return gross - loss - pin
}

/**
 * Calculate next available bar number
 * @param {Array} manifestBars - Bars in current manifest
 * @param {Array} allBars - All bars in system
 * @returns {number} - Next available bar number
 */
export const calculateNextBarNumber = (manifestBars, allBars) => {
  const manifestNums = manifestBars.map(b => b.barNumber).filter(n => n != null)
  const existingNums = (allBars || []).map(b => parseInt(b.barNumber, 10)).filter(n => !isNaN(n))
  return Math.max(0, ...manifestNums, ...existingNums) + 1
}

/**
 * Extract bar number from serial number (e.g., 'RL558-1' -> 1)
 * Used to convert full serial numbers to simple numeric barNumber for data keys
 * @param {string} serialNumber - Full serial number (e.g., 'RL558-1')
 * @returns {number|null} - Extracted bar number or null if invalid
 */
export const extractBarNumber = (serialNumber) => {
  const parts = String(serialNumber).split('-')
  const num = parseInt(parts[parts.length - 1])
  return isNaN(num) ? null : num
}

/**
 * Calculate assay loss for a bar if not already set
 * Returns existing assay loss if present, otherwise calculates from fire results
 * @param {object} bar - Bar object with barNumber and optional assayLoss
 * @param {object} fireResults - Fire results keyed by barNumber
 * @param {boolean} isPrimarySilver - Whether primary metal is silver
 * @returns {number|null} - Assay loss in troy ounces, or null if no loss
 */
export const calculateAssayLossIfNeeded = (bar, fireResults, isPrimarySilver) => {
  const existingAssayLoss = extractDisplayWeight(bar.assayLoss, 'troyOunces') || 0
  // Only auto-calculate if assay loss is not already set (or is 0)
  if (existingAssayLoss && existingAssayLoss > 0) {
    return existingAssayLoss
  }
  
  if (fireResults) {
    const calculatedLoss = calculateAssayLossFromFireResults(bar.barNumber, fireResults, isPrimarySilver)
    if (calculatedLoss > 0) {
      return calculatedLoss
    }
  }
  
  return existingAssayLoss || null
}
